/**
 * HW Cart Drawer — Editor integration + optional peek shell sync.
 *
 * Peek sync runs only when the section sets `data-hw-drawer-peek-sync` (desktop
 * inset > 0, or distinct desktop corner radius). Listeners attach while the
 * drawer is open and are removed on close.
 */

/** @param {string} sectionId */
function getDrawerForSection(sectionId) {
  return /** @type {import('./cart-drawer.js').CartDrawerComponent | null} */ (
    document.querySelector(`cart-drawer-component[data-section-id="${sectionId}"]`)
  );
}

document.addEventListener('shopify:section:select', (event) => {
  const drawer = getDrawerForSection(/** @type {any} */ (event).detail.sectionId);
  if (drawer) drawer.open();
});

document.addEventListener('shopify:section:deselect', (event) => {
  const drawer = getDrawerForSection(/** @type {any} */ (event).detail.sectionId);
  if (drawer) drawer.close();
});

/** @type {WeakMap<HTMLDialogElement, AbortController>} */
const peekSyncControllers = new WeakMap();

/**
 * @param {HTMLElement} root
 * @param {number} fallback
 * @returns {number}
 */
function readDrawerWidthPercent(root, fallback) {
  const raw = root.dataset.hwDrawerWidthPercent;
  if (raw == null || raw === '') {
    return fallback;
  }

  const num = parseFloat(raw);
  return Number.isFinite(num) ? num : fallback;
}

/**
 * @param {HTMLElement} root
 * @param {number} fallback
 * @returns {number}
 */
function readDrawerMaxWidthPx(root, fallback) {
  const raw = root.dataset.hwDrawerMaxWidth;
  if (raw == null || raw === '') {
    return fallback;
  }

  const num = parseFloat(raw);
  return Number.isFinite(num) ? num : fallback;
}

/**
 * Whether the drawer is configured narrower than the viewport (page peek).
 *
 * @param {HTMLElement} drawerRoot
 * @returns {boolean}
 */
function cartDrawerHasIntrinsicPeek(drawerRoot) {
  const maxWidth = readDrawerMaxWidthPx(drawerRoot, 480);
  const widthPercent = readDrawerWidthPercent(drawerRoot, 85);
  const viewportWidth = document.documentElement.clientWidth;
  const widthFromVw = (viewportWidth * widthPercent) / 100;
  const slotWidth = Math.min(maxWidth, widthFromVw);

  return slotWidth < viewportWidth - 1;
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {HTMLElement} drawerRoot
 */
function syncCartDrawerPeek(dialog, drawerRoot) {
  if (!dialog.open) {
    dialog.classList.remove('cart-drawer--has-peek');
    return;
  }

  const shouldPeek = cartDrawerHasIntrinsicPeek(drawerRoot);
  const hasPeek = dialog.classList.contains('cart-drawer--has-peek');

  if (shouldPeek !== hasPeek) {
    dialog.classList.toggle('cart-drawer--has-peek', shouldPeek);
  }
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {HTMLElement} drawerRoot
 */
function startCartDrawerPeekSync(dialog, drawerRoot) {
  peekSyncControllers.get(dialog)?.abort();

  const controller = new AbortController();
  const { signal } = controller;
  peekSyncControllers.set(dialog, controller);

  let resizeRaf = 0;

  const update = () => {
    syncCartDrawerPeek(dialog, drawerRoot);
  };

  const onResize = () => {
    if (resizeRaf) {
      return;
    }

    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      update();
    });
  };

  window.addEventListener('resize', onResize, { passive: true, signal });
  signal.addEventListener('abort', () => {
    window.removeEventListener('resize', onResize);
    if (resizeRaf) {
      cancelAnimationFrame(resizeRaf);
    }
  });

  requestAnimationFrame(update);
}

/**
 * @param {HTMLElement} drawer
 */
function wireCartDrawerPeek(drawer) {
  if (!drawer.hasAttribute('data-hw-drawer-peek-sync')) {
    return;
  }

  if (drawer.dataset.hwDrawerPeekWired === 'true') {
    return;
  }

  const dialog = drawer.querySelector('dialog.cart-drawer__dialog');
  if (!(dialog instanceof HTMLDialogElement)) {
    return;
  }

  drawer.dataset.hwDrawerPeekWired = 'true';

  drawer.addEventListener(
    'dialog:open',
    () => {
      startCartDrawerPeekSync(dialog, drawer);
    },
    { passive: true }
  );

  drawer.addEventListener(
    'dialog:close',
    () => {
      peekSyncControllers.get(dialog)?.abort();
      peekSyncControllers.delete(dialog);
      dialog.classList.remove('cart-drawer--has-peek');
    },
    { passive: true }
  );

  // Also listen to the native close event for cases where the dialog is
  // dismissed natively (e.g. ESC key) — the native event fires on the dialog
  // directly and does not bubble, so dialog:close on the parent won't fire.
  dialog.addEventListener(
    'close',
    () => {
      peekSyncControllers.get(dialog)?.abort();
      peekSyncControllers.delete(dialog);
      dialog.classList.remove('cart-drawer--has-peek');
    },
    { passive: true }
  );

  if (dialog.open) {
    startCartDrawerPeekSync(dialog, drawer);
  }
}

for (const drawer of document.querySelectorAll('cart-drawer-component[data-hw-drawer-peek-sync]')) {
  wireCartDrawerPeek(/** @type {HTMLElement} */ (drawer));
}

document.addEventListener('shopify:section:load', (event) => {
  const section = /** @type {any} */ (event).target;
  const drawer = section?.querySelector?.('cart-drawer-component[data-hw-drawer-peek-sync]');
  if (drawer) {
    wireCartDrawerPeek(drawer);
  }
});

/**
 * Full section morphs (cart:update from Glowwa plan swap, empty-cart ATC,
 * upsells, etc.) replace dialog classes from server HTML — which never
 * includes the runtime `cart-drawer--has-peek` class. Without re-sync the
 * desktop inset (drawer_desktop_inset) disappears until resize/reopen.
 * Defer one frame so cart-items-component can finish morphing first.
 */
document.addEventListener('cart:update', () => {
  requestAnimationFrame(() => {
    for (const drawer of document.querySelectorAll(
      'cart-drawer-component[data-hw-drawer-peek-sync]'
    )) {
      const dialog = drawer.querySelector('dialog.cart-drawer__dialog');
      if (!(dialog instanceof HTMLDialogElement) || !dialog.open) {
        continue;
      }

      syncCartDrawerPeek(dialog, /** @type {HTMLElement} */ (drawer));

      if (!peekSyncControllers.has(dialog)) {
        startCartDrawerPeekSync(dialog, /** @type {HTMLElement} */ (drawer));
      }
    }
  });
});

/**
 * HW Quantity Dropdown
 *
 * Handles change events on .hw-quantity-dropdown <select> elements and
 * dispatches the same 'quantity-selector:update' custom event that the
 * built-in quantity-selector-component emits. This allows cart-items-component
 * to process dropdown quantity changes identically to stepper changes.
 *
 * composed: true ensures the event crosses shadow DOM boundaries.
 */
document.addEventListener('change', (event) => {
  const select = /** @type {HTMLSelectElement | null} */ (event.target);
  if (!select || !select.classList.contains('hw-quantity-dropdown')) return;

  const cartLine = Number(select.dataset.cartLine);
  const quantity = Number(select.value);

  if (!cartLine || isNaN(quantity)) return;

  select.dispatchEvent(
    new CustomEvent('quantity-selector:update', {
      bubbles: true,
      composed: true,
      detail: { quantity, cartLine },
    })
  );
});
